import React, { memo, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import _ from "lodash";
import classNames from "classnames";
import { Avatar, Tooltip } from "@toddle-edu/ds-web";
import { Virtuoso } from "react-virtuoso";
import SkeletonLoader from "UIComponents/SkeletonLoader";
import useConditionalTooltip from "TranscriptsV3/hooks/useConditionalTooltip";
import classes from "./StudentsList.scss";

const StudentListItem = ({
  id,
  isLastItem,
  name,
  profileImage,
  showLoader,
}) => {
  const textWrapperRef = useRef(null);
  const isTooltipVisible = useConditionalTooltip({ textWrapperRef });

  if (showLoader) {
    return (
      <div
        className={classNames(classes.listItem, {
          [classes.lastItem]: isLastItem,
        })}
      >
        <div>
          <SkeletonLoader height="24px" width="24px" variant="circular" />
        </div>
        <div className={classes.textSkeleton}>
          <SkeletonLoader height="20px" width="100%" />
        </div>
      </div>
    );
  }

  return (
    <div
      className={classNames(classes.listItem, {
        [classes.lastItem]: isLastItem,
      })}
    >
      <Avatar
        id={id}
        name={name}
        src={profileImage}
        shape="circle"
        size="x-small"
      />
      <Tooltip visible={isTooltipVisible} tooltip={name}>
        <div className="text-body truncate" ref={textWrapperRef}>
          {name}
        </div>
      </Tooltip>
    </div>
  );
};

const StudentListContent = ({ count, isData, isLoading, students }) => {
  const showLoader = !isData && isLoading;

  const items = useMemo(() => {
    if (showLoader) return _.times(count, i => ({ id: `skeleton-${i}` }));
    return students;
  }, [count, showLoader, students]);

  const styles = useMemo(
    () => ({
      height: Math.min(items.length * 40, 276),
    }),
    [items.length]
  );

  const handleClick = event => {
    event.stopPropagation();
  };

  const handleKeyDown = event => {
    event.stopPropagation();
  };

  return (
    <Virtuoso
      className={classes.container}
      style={styles}
      data={items}
      itemContent={(index, { id, name, profileImage }) => (
        <StudentListItem
          showLoader={showLoader}
          name={name}
          profileImage={profileImage}
          id={id}
          isLastItem={index === items.length - 1}
        />
      )}
      computeItemKey={(index, item) => item.id || index}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    />
  );
};

StudentListItem.displayName = "StudentListItem";
StudentListContent.displayName = "StudentListContent";

StudentListContent.propTypes = {
  count: PropTypes.number,
  isData: PropTypes.bool,
  isLoading: PropTypes.bool,
  students: PropTypes.arrayOf(PropTypes.object),
};

StudentListContent.defaultProps = {
  count: 0,
  isData: false,
  isLoading: false,
  students: [],
};

export default memo(StudentListContent);
